import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { cn } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import Dice3DOverlay from '../components/dice/Dice3DOverlay';
import { useRemoteDiceRoll } from '../components/dice/useRemoteDiceRoll';
import { 
  Swords, Heart, Shield, User, Skull, AlertTriangle, 
  EyeOff, Moon, Snowflake, Bug, Activity, Sparkles,
  Crown, ChevronRight, Zap, Volume2, VolumeX
} from 'lucide-react';

const API_URL = '/api';

// Condition icons (subset visible to players)
const CONDITION_ICONS = {
  blinded: EyeOff,
  frightened: AlertTriangle,
  paralyzed: Snowflake,
  poisoned: Bug,
  stunned: Sparkles,
  unconscious: Moon,
  prone: ChevronRight,
  exhaustion: Activity,
  concentration: Zap
};

const CONDITION_LABELS = {
  blinded: { fr: 'Aveuglé', en: 'Blinded' },
  charmed: { fr: 'Charmé', en: 'Charmed' },
  deafened: { fr: 'Assourdi', en: 'Deafened' },
  frightened: { fr: 'Effrayé', en: 'Frightened' },
  grappled: { fr: 'Agrippé', en: 'Grappled' },
  incapacitated: { fr: 'Incapable', en: 'Incapacitated' },
  invisible: { fr: 'Invisible', en: 'Invisible' },
  paralyzed: { fr: 'Paralysé', en: 'Paralyzed' },
  petrified: { fr: 'Pétrifié', en: 'Petrified' },
  poisoned: { fr: 'Empoisonné', en: 'Poisoned' },
  prone: { fr: 'À terre', en: 'Prone' },
  restrained: { fr: 'Entravé', en: 'Restrained' },
  stunned: { fr: 'Étourdi', en: 'Stunned' },
  unconscious: { fr: 'Inconscient', en: 'Unconscious' },
  exhaustion: { fr: 'Épuisement', en: 'Exhaustion' },
  concentration: { fr: 'Concentration', en: 'Concentration' }
};

const HP_STATUS_COLORS = {
  healthy: { bg: 'bg-green-500', text: 'text-green-500', label: { fr: 'En forme', en: 'Healthy' } },
  injured: { bg: 'bg-yellow-500', text: 'text-yellow-500', label: { fr: 'Blessé', en: 'Injured' } },
  bloodied: { bg: 'bg-orange-500', text: 'text-orange-500', label: { fr: 'Ensanglanté', en: 'Bloodied' } },
  critical: { bg: 'bg-red-500', text: 'text-red-500', label: { fr: 'Critique', en: 'Critical' } },
  down: { bg: 'bg-gray-500', text: 'text-gray-500', label: { fr: 'À terre', en: 'Down' } }
};

export default function PlayerViewPage() {
  const { shareCode } = useParams();
  const { activeRoll, observeRoll, clearActiveRoll } = useRemoteDiceRoll(shareCode);
  const [encounter, setEncounter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [language] = useState('fr');
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [lastTurnIndex, setLastTurnIndex] = useState(null);

  const fetchEncounter = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/combat/player-view/${shareCode}`);
      const newEncounter = response.data;
      observeRoll(newEncounter.latest_public_roll || null);
      
      // Play sound on turn change (if enabled)
      if (soundEnabled && lastTurnIndex !== null && newEncounter.current_turn_index !== lastTurnIndex) {
        // Simple beep using Web Audio API
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          oscillator.frequency.value = 800;
          oscillator.type = 'sine';
          gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.3);
        } catch (e) {
          console.log('Audio not available');
        }
      }
      
      setLastTurnIndex(newEncounter.current_turn_index);
      setEncounter(newEncounter);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        setError(language === 'fr' ? 'Combat non trouvé' : 'Combat not found');
      } else if (err.response?.status === 403) {
        setError(language === 'fr' ? 'Le combat n\'est pas actif' : 'Combat is not active');
      } else {
        setError(language === 'fr' ? 'Erreur de connexion' : 'Connection error');
      }
    } finally {
      setLoading(false);
    }
  }, [shareCode, language, soundEnabled, lastTurnIndex, observeRoll]);

  // Initial fetch
  useEffect(() => {
    fetchEncounter();
  }, [fetchEncounter]);

  // SSE with polling fallback
  useEffect(() => {
    let interval = null;
    let es = null;
    try {
      es = new EventSource(`${API_URL}/combat/player-view/${shareCode}/sse`);
      es.addEventListener('update', () => fetchEncounter());
      es.onerror = () => {
        es.close();
        es = null;
        if (!interval) interval = setInterval(fetchEncounter, 3000);
      };
    } catch {
      interval = setInterval(fetchEncounter, 3000);
    }
    return () => {
      if (es) es.close();
      if (interval) clearInterval(interval);
    };
  }, [fetchEncounter, shareCode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Swords className="h-16 w-16 animate-pulse text-primary mx-auto mb-4" />
          <p className="text-xl text-white">{language === 'fr' ? 'Chargement...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Swords className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-xl text-white mb-2">{error}</p>
          <p className="text-muted-foreground">
            {language === 'fr' ? 'Vérifiez le code de partage' : 'Check the share code'}
          </p>
        </div>
      </div>
    );
  }

  if (!encounter) return null;

  // Filter combatants: hide all dead (confirmed), keep unconscious players visible
  const visibleCombatants = encounter.combatants.filter(c => {
    // Hide ALL dead combatants (including players with confirmed death)
    if (c.status === 'dead') return false;
    // Show unconscious players (for death saves) and all active combatants
    return true;
  });

  // current_turn_index is based on living combatants order
  const currentCombatant = visibleCombatants[encounter.current_turn_index];
  const isCurrentVisible = visibleCombatants.some(c => c.id === currentCombatant?.id);

  // Count eliminated enemies (for flavor)
  const eliminatedCount = encounter.combatants.filter(
    c => c.status === 'dead' && c.combatant_type !== 'player'
  ).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Swords className="h-8 w-8 text-primary" />
          <h1 className="text-3xl md:text-4xl font-bold text-white tracking-wide">
            {encounter.encounter_name}
          </h1>
        </div>
        {encounter.location && (
          <p className="text-lg text-muted-foreground">{encounter.location}</p>
        )}
        <div className="flex items-center justify-center gap-4 mt-4 flex-wrap">
          <Badge className="text-lg px-4 py-1 bg-primary">
            Round {encounter.current_round}
          </Badge>
          <Badge variant="outline" className="text-lg px-4 py-1 text-white border-white/30">
            {visibleCombatants.length} {language === 'fr' ? 'combattants' : 'combatants'}
          </Badge>
          {eliminatedCount > 0 && (
            <Badge variant="outline" className="text-lg px-4 py-1 text-red-400 border-red-400/30">
              <Skull className="h-4 w-4 mr-1" />
              {eliminatedCount} {language === 'fr' ? 'éliminés' : 'eliminated'}
            </Badge>
          )}
          
          {/* Sound Toggle */}
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={cn(
              "flex items-center gap-1 px-3 py-1 rounded-full text-sm transition-colors",
              soundEnabled 
                ? "bg-primary/20 text-primary" 
                : "bg-white/10 text-muted-foreground hover:bg-white/20"
            )}
          >
            {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            {language === 'fr' ? 'Son' : 'Sound'}
          </button>
        </div>
      </div>

      {/* Current Turn Highlight */}
      {currentCombatant && isCurrentVisible && (
        <div className="max-w-2xl mx-auto mb-8">
          <Card className={cn(
            "border-2 shadow-lg",
            currentCombatant.status === 'unconscious' 
              ? "bg-yellow-500/20 border-yellow-500 shadow-yellow-500/20"
              : "bg-primary/20 border-primary shadow-primary/20"
          )}>
            <CardContent className="p-6 text-center">
              <p className={cn(
                "text-sm mb-1 uppercase tracking-wider",
                currentCombatant.status === 'unconscious' ? "text-yellow-500" : "text-primary"
              )}>
                {language === 'fr' ? 'Tour actuel' : 'Current Turn'}
              </p>
              <div className="flex items-center justify-center gap-3">
                {currentCombatant.status === 'unconscious' ? (
                  <Moon className="h-8 w-8 text-yellow-500" />
                ) : (
                  <Crown className="h-8 w-8 text-primary" />
                )}
                <h2 className="text-3xl font-bold text-white">{currentCombatant.name}</h2>
                {currentCombatant.status === 'unconscious' && (
                  <Badge className="bg-yellow-500 text-black">
                    {language === 'fr' ? 'Inconscient' : 'Unconscious'}
                  </Badge>
                )}
              </div>
              {currentCombatant.combatant_type === 'player' && currentCombatant.status === 'unconscious' && (
                <p className="text-yellow-400/80 text-sm mt-2">
                  {language === 'fr' 
                    ? '⚠️ Jets de sauvegarde contre la mort requis' 
                    : '⚠️ Death saving throws required'}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Initiative Tracker */}
      <div className="max-w-4xl mx-auto">
        <div className="grid gap-3">
          {visibleCombatants.map((combatant) => {
            const isCurrentTurn = currentCombatant?.id === combatant.id;
            const isUnconscious = combatant.status === 'unconscious';
            const isDown = combatant.hp_status === 'down' || combatant.current_hp <= 0;
            
            // HP display
            let hpDisplay;
            if (encounter.hp_display_mode === 'exact') {
              const hpPercent = Math.max(0, (combatant.current_hp / combatant.max_hp) * 100);
              hpDisplay = (
                <div className="flex items-center gap-2 flex-1">
                  <Heart className={cn(
                    "h-5 w-5",
                    isDown ? "text-gray-500" :
                    hpPercent > 50 ? "text-green-500" :
                    hpPercent > 25 ? "text-yellow-500" : "text-red-500"
                  )} />
                  <div className="flex-1 relative">
                    <Progress value={hpPercent} className="h-4" />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white drop-shadow">
                      {combatant.current_hp}/{combatant.max_hp}
                      {combatant.temp_hp > 0 && ` (+${combatant.temp_hp})`}
                    </span>
                  </div>
                </div>
              );
            } else {
              // Status mode
              const status = HP_STATUS_COLORS[combatant.hp_status] || HP_STATUS_COLORS.healthy;
              hpDisplay = (
                <Badge className={cn("text-sm px-3", status.bg)}>
                  {status.label[language]}
                </Badge>
              );
            }

            return (
              <Card 
                key={combatant.id}
                className={cn(
                  "transition-all duration-300",
                  isCurrentTurn && !isUnconscious && "ring-2 ring-primary shadow-lg shadow-primary/30 scale-[1.02]",
                  isCurrentTurn && isUnconscious && "ring-2 ring-yellow-500 shadow-lg shadow-yellow-500/30 scale-[1.02]",
                  isUnconscious && "opacity-70 border-yellow-500/50"
                )}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Initiative Badge */}
                    <div className={cn(
                      "w-14 h-14 rounded-xl flex flex-col items-center justify-center shrink-0 font-bold",
                      isCurrentTurn && !isUnconscious && "bg-primary text-primary-foreground",
                      isCurrentTurn && isUnconscious && "bg-yellow-500 text-black",
                      !isCurrentTurn && combatant.combatant_type === 'player' && "bg-blue-500/20 border-2 border-blue-500",
                      !isCurrentTurn && combatant.combatant_type !== 'player' && "bg-secondary"
                    )}>
                      <span className="text-2xl">{combatant.initiative}</span>
                    </div>

                    {/* Name and Type */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={cn(
                          "text-lg font-semibold text-white",
                          isUnconscious && "text-yellow-400"
                        )}>
                          {combatant.name}
                        </h3>
                        {combatant.combatant_type === 'player' && (
                          <Badge variant="outline" className="text-xs border-blue-500 text-blue-400">
                            <User className="h-3 w-3 mr-1" />
                            {language === 'fr' ? 'Joueur' : 'Player'}
                          </Badge>
                        )}
                        {isUnconscious && (
                          <Badge className="bg-yellow-500 text-black text-xs">
                            <Moon className="h-3 w-3 mr-1" />
                            {language === 'fr' ? 'Inconscient' : 'Unconscious'}
                          </Badge>
                        )}
                      </div>

                      {/* Conditions */}
                      {combatant.status_effects?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {combatant.status_effects.map((effect, i) => {
                            const Icon = CONDITION_ICONS[effect.name] || AlertTriangle;
                            const label = CONDITION_LABELS[effect.name]?.[language] || effect.name;
                            return (
                              <Badge 
                                key={i}
                                variant="outline" 
                                className="text-xs text-orange-400 border-orange-400/50"
                              >
                                <Icon className="h-3 w-3 mr-1" />
                                {label}
                                {effect.duration && ` (${effect.duration})`}
                              </Badge>
                            );
                          })}
                        </div>
                      )}

                      {combatant.system_stats?.some(field => !['health', 'initiative'].includes(field.role)) && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {combatant.system_stats
                            .filter(field => !['health', 'initiative'].includes(field.role))
                            .map((field, index) => (
                              <Badge key={field.id || index} variant="secondary" className="text-xs">
                                {field.label}: {String(field.value ?? '-')}
                              </Badge>
                            ))}
                        </div>
                      )}

                      {/* Death save reminder for unconscious players */}
                      {combatant.combatant_type === 'player' && isUnconscious && (
                        <p className="text-xs text-yellow-400/70 mt-1">
                          {language === 'fr' ? '⚠️ Jets de sauvegarde contre la mort' : '⚠️ Death saving throws'}
                        </p>
                      )}
                    </div>

                    {/* HP Display */}
                    <div className="shrink-0 w-36">
                      {hpDisplay}
                    </div>

                    {/* AC (only for players) */}
                    {combatant.combatant_type === 'player' && combatant.ac && (
                      <div className="flex items-center gap-1 shrink-0 text-muted-foreground">
                        <Shield className="h-5 w-5" />
                        <span className="font-bold">{combatant.ac}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center mt-8 text-muted-foreground text-sm">
        <p>{language === 'fr' ? 'Mise à jour automatique toutes les 3 secondes' : 'Auto-refresh every 3 seconds'}</p>
        <p className="mt-1 opacity-50">Code: {shareCode}</p>
      </div>
      {activeRoll && (
        <Dice3DOverlay rollData={activeRoll} onComplete={clearActiveRoll} />
      )}
    </div>
  );
}

